import { computed, ref } from "vue";

import { PlainlyEncodingFormat, PlainlyPostEncoding } from "@/utils/CreativeIntelligenceApi/plainly-options";

import { DEFAULT_OUTPUT_MODULE } from "@/components/Download/consts";
import type { EncodingFormatType, OutputModuleType, PlainlyOutputOptions, PostEncodingType } from "@/types/types";

const DEFAULT_SCALING_PERCENTAGE = 100;
const DEFAULT_ENCODING_PARAMS_LINE = "";
const DEFAULT_ENCODING_FORMAT = PlainlyEncodingFormat.MP4;
const DEFAULT_POST_ENCODING_TYPE = PlainlyPostEncoding.DEFAULT;

type ModelValue = {
    scalingPercentage: number;
    encodingFormat: EncodingFormatType;
    outputModule: OutputModuleType | typeof DEFAULT_OUTPUT_MODULE;
    postEncoding: PostEncodingType;
    encodingParamsLine?: string;
};

const modelValue = ref<ModelValue>({
    scalingPercentage: DEFAULT_SCALING_PERCENTAGE,
    encodingFormat: DEFAULT_ENCODING_FORMAT,
    outputModule: DEFAULT_OUTPUT_MODULE,
    postEncoding: DEFAULT_POST_ENCODING_TYPE,
    encodingParamsLine: DEFAULT_ENCODING_PARAMS_LINE
});

export function useDownloadAEVideo() {
    const scalingPercentageError = computed(() => {
        if (modelValue.value.scalingPercentage < 1 || modelValue.value.scalingPercentage > 400) {
            return "Scaling percentage must be between 1 and 400.";
        }

        return "";
    });

    const isButtonDisabled = computed(() => scalingPercentageError.value !== "");

    function resetValues() {
        modelValue.value = {
            scalingPercentage: DEFAULT_SCALING_PERCENTAGE,
            encodingFormat: DEFAULT_ENCODING_FORMAT,
            outputModule: DEFAULT_OUTPUT_MODULE,
            postEncoding: DEFAULT_POST_ENCODING_TYPE,
            encodingParamsLine: DEFAULT_ENCODING_PARAMS_LINE
        };
    }

    function computeAEOutput() {
        const output: Omit<PlainlyOutputOptions, "preview"> = {};

        if (modelValue.value.outputModule !== DEFAULT_OUTPUT_MODULE) {
            output.outputModule = modelValue.value.outputModule;
        }

        const { postEncoding, scalingPercentage, encodingFormat, encodingParamsLine } = modelValue.value;

        if (
            postEncoding === PlainlyPostEncoding.DEFAULT ||
            postEncoding === PlainlyPostEncoding.NONE ||
            postEncoding === PlainlyPostEncoding.SMALLEST
        ) {
            output.postEncoding = {
                type: postEncoding
            };
        }

        if (postEncoding === PlainlyPostEncoding.SCALE && scalingPercentage) {
            output.postEncoding = {
                type: PlainlyPostEncoding.SCALE,
                scalingPercentage: scalingPercentage
            };
        }

        if (postEncoding === PlainlyPostEncoding.CUSTOM) {
            output.postEncoding = {
                type: PlainlyPostEncoding.CUSTOM,
                encodingFormat: encodingFormat,
                ...(encodingParamsLine ? { encodingParamsLine } : {})
            };
        }

        return output;
    }

    return {
        scalingPercentageError,
        modelValue,
        computeAEOutput,
        resetValues,
        isButtonDisabled
    };
}
